import { useCallback, useEffect, useState } from "react";
import axios from "axios";

import api from "../../lib/api";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { Modal } from "../ui/Modal";

interface Empresa {
  id: number;
  idExterno: string | null;
  razonSocial: string;
  nombreComercial: string | null;
  cif: string | null;
  emailContacto: string | null;
  estadoComercial: "prospect" | "cliente" | "inactivo";
}

interface EmpresasResponse {
  data: Empresa[];
}

interface Contacto {
  id: number;
  nombre: string;
  activo: boolean;
}

interface Oportunidad {
  id: number;
  idExterno: string | null;
  titulo: string;
  estado: "abierta" | "ganada" | "perdida";
}

interface RelacionesResponse {
  oportunidades: Oportunidad[];
}

interface CalendarStatus {
  connected: boolean;
}

interface CreatedRecord {
  id: number;
}

const CRM_FOLLOW_UP_UPDATED_EVENT = "crm:seguimiento-actualizado";

function notifyCrmFollowUpUpdated(): void {
  window.dispatchEvent(new Event(CRM_FOLLOW_UP_UPDATED_EVENT));
}

function getErrorMessage(
  error: unknown,
  fallback = "No se pudo completar la operación"
): string {
  if (axios.isAxiosError<{ error?: string }>(error)) {
    return error.response?.data?.error ?? fallback;
  }
  return fallback;
}

function currentLocalDateTime(): string {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

export function RegistrarActividadGlobalModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [empresaSearch, setEmpresaSearch] = useState("");
  const [empresaPickerOpen, setEmpresaPickerOpen] = useState(false);
  const [empresasLoading, setEmpresasLoading] = useState(false);
  const [contactos, setContactos] = useState<Contacto[]>([]);
  const [oportunidades, setOportunidades] = useState<Oportunidad[]>([]);
  const [empresaId, setEmpresaId] = useState("");
  const [tipo, setTipo] = useState("llamada");
  const [asunto, setAsunto] = useState("");
  const [detalle, setDetalle] = useState("");
  const [realizadaAt, setRealizadaAt] = useState(currentLocalDateTime());
  const [contactoId, setContactoId] = useState("");
  const [oportunidadId, setOportunidadId] = useState("");
  const [calendarConnected, setCalendarConnected] = useState(false);
  const [createTask, setCreateTask] = useState(false);
  const [activityCreatedId, setActivityCreatedId] = useState<number | null>(null);
  const [completedMessage, setCompletedMessage] = useState("");
  const [taskForm, setTaskForm] = useState({
    tipo: "tarea" as "tarea" | "llamada" | "reunion" | "email",
    titulo: "",
    detalle: "",
    venceAt: "",
    recordatorioAt: "",
    prioridad: "normal" as "baja" | "normal" | "alta",
    syncCalendar: false,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const reset = useCallback(() => {
    setEmpresas([]);
    setEmpresaSearch("");
    setEmpresaPickerOpen(false);
    setEmpresaId("");
    setTipo("llamada");
    setAsunto("");
    setDetalle("");
    setRealizadaAt(currentLocalDateTime());
    setContactoId("");
    setOportunidadId("");
    setContactos([]);
    setOportunidades([]);
    setCalendarConnected(false);
    setCreateTask(false);
    setActivityCreatedId(null);
    setCompletedMessage("");
    setTaskForm({
      tipo: "tarea",
      titulo: "",
      detalle: "",
      venceAt: "",
      recordatorioAt: "",
      prioridad: "normal",
      syncCalendar: false,
    });
    setError("");
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    reset();
    setLoading(true);
    api
      .get<CalendarStatus>("/admin/crm/calendar/status")
      .then((calendarResponse) => {
        setCalendarConnected(calendarResponse.data.connected);
      })
      .catch(() => setCalendarConnected(false))
      .finally(() => setLoading(false));
  }, [isOpen, reset]);

  useEffect(() => {
    if (!isOpen || activityCreatedId !== null) return;

    let cancelled = false;
    setEmpresasLoading(true);
    const timer = window.setTimeout(() => {
      api
        .get<EmpresasResponse>("/admin/crm/clientes", {
          params: {
            page: 1,
            limit: 20,
            estado: "activo",
            search: empresaSearch.trim() || undefined,
          },
        })
        .then((response) => {
          if (!cancelled) setEmpresas(response.data.data);
        })
        .catch((loadError: unknown) => {
          if (!cancelled) {
            setEmpresas([]);
            setError(getErrorMessage(loadError, "No se pudieron buscar empresas"));
          }
        })
        .finally(() => {
          if (!cancelled) setEmpresasLoading(false);
        });
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [activityCreatedId, empresaSearch, isOpen]);

  useEffect(() => {
    if (!isOpen || !empresaId) {
      setContactos([]);
      setOportunidades([]);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError("");

    Promise.all([
      api.get<{ data: Contacto[] }>(
        `/admin/crm/contactos/empresa/${empresaId}`
      ),
      api.get<RelacionesResponse>(
        `/admin/crm/seguimiento/empresa/${empresaId}`
      ),
    ])
      .then(([contactsResponse, relationsResponse]) => {
        if (cancelled) return;
        setContactos(
          contactsResponse.data.data.filter((contacto) => contacto.activo)
        );
        setOportunidades(relationsResponse.data.oportunidades);
      })
      .catch((loadError: unknown) => {
        if (!cancelled) setError(getErrorMessage(loadError));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [empresaId, isOpen]);

  const close = () => {
    if (saving) return;
    onClose();
  };

  const selectEmpresa = (empresa: Empresa) => {
    setEmpresaId(String(empresa.id));
    setEmpresaSearch(
      `${empresa.nombreComercial || empresa.razonSocial}${
        empresa.idExterno ? ` · ${empresa.idExterno}` : ""
      }`
    );
    setEmpresaPickerOpen(false);
    setContactoId("");
    setOportunidadId("");
  };

  const save = async () => {
    setError("");
    if (!empresaId || !asunto.trim() || !realizadaAt) {
      setError("Selecciona la empresa e indica el resumen y la fecha.");
      return;
    }
    if (createTask && (!taskForm.titulo.trim() || !taskForm.venceAt)) {
      setError("Indica el título y la fecha de la próxima acción.");
      return;
    }
    if (
      createTask &&
      taskForm.recordatorioAt &&
      new Date(taskForm.recordatorioAt) >= new Date(taskForm.venceAt)
    ) {
      setError("El aviso previo debe ser anterior a la próxima acción.");
      return;
    }

    let actividadId = activityCreatedId;
    setSaving(true);
    try {
      if (actividadId === null) {
        const activityResponse = await api.post<CreatedRecord>(
          `/admin/crm/seguimiento/actividades/empresa/${empresaId}`,
          {
            tipo,
            asunto,
            detalle: detalle || null,
            realizada_at: new Date(realizadaAt).toISOString(),
            contacto_id: contactoId ? Number(contactoId) : null,
            oportunidad_id: oportunidadId ? Number(oportunidadId) : null,
          }
        );
        actividadId = activityResponse.data.id;
        setActivityCreatedId(actividadId);
      }

      if (createTask) {
        const taskResponse = await api.post<CreatedRecord>(
          `/admin/crm/seguimiento/tareas/empresa/${empresaId}`,
          {
            tipo: taskForm.tipo,
            titulo: taskForm.titulo,
            detalle: taskForm.detalle || null,
            vence_at: new Date(taskForm.venceAt).toISOString(),
            recordatorio_at: taskForm.recordatorioAt
              ? new Date(taskForm.recordatorioAt).toISOString()
              : null,
            prioridad: taskForm.prioridad,
            contacto_id: contactoId ? Number(contactoId) : null,
            oportunidad_id: oportunidadId ? Number(oportunidadId) : null,
            actividad_origen_id: actividadId,
          }
        );

        if (taskForm.syncCalendar) {
          try {
            await api.post(
              `/admin/crm/calendar/tasks/${taskResponse.data.id}/sync`
            );
          } catch (calendarError) {
            notifyCrmFollowUpUpdated();
            setCompletedMessage(
              "La actividad y la próxima acción están guardadas en el CRM."
            );
            setError(
              `${getErrorMessage(
                calendarError,
                "No se pudo añadir el evento a Google Calendar"
              )}. Puedes reintentarlo desde Agenda.`
            );
            return;
          }
        }
      }
      notifyCrmFollowUpUpdated();
      onClose();
    } catch (saveError) {
      setError(
        actividadId === null
          ? getErrorMessage(saveError)
          : `${getErrorMessage(
              saveError,
              "No se pudo crear la próxima acción"
            )}. La actividad ya está guardada y no se duplicará al reintentar.`
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      title="Registrar actividad"
      size="lg"
      footer={
        completedMessage ? (
          <Button onClick={close}>Cerrar</Button>
        ) : (
          <>
            <Button variant="secondary" onClick={close} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={save} loading={saving}>
              {createTask ? "Guardar actividad y tarea" : "Guardar actividad"}
            </Button>
          </>
        )
      }
    >
      <div className="space-y-5">
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
          Registra la interacción y, si hace falta seguimiento, crea también la
          próxima acción sin salir de esta ventana.
        </div>

        {completedMessage && (
          <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800">
            {completedMessage}
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            {error}
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-2">
          <div className="relative md:col-span-2">
            <Input
              label="Empresa *"
              placeholder="Buscar por empresa, ID, CIF o email"
              value={empresaSearch}
              role="combobox"
              aria-expanded={empresaPickerOpen}
              aria-controls="crm-empresa-resultados"
              autoComplete="off"
              disabled={activityCreatedId !== null}
              onFocus={() => setEmpresaPickerOpen(true)}
              onBlur={() => {
                window.setTimeout(() => setEmpresaPickerOpen(false), 150);
              }}
              onKeyDown={(event) => {
                if (event.key === "Escape") {
                  setEmpresaPickerOpen(false);
                } else if (
                  event.key === "Enter" &&
                  empresaPickerOpen &&
                  empresas.length === 1
                ) {
                  event.preventDefault();
                  selectEmpresa(empresas[0]);
                }
              }}
              onChange={(event) => {
                setEmpresaSearch(event.target.value);
                setEmpresaPickerOpen(true);
                if (empresaId) {
                  setEmpresaId("");
                  setContactoId("");
                  setOportunidadId("");
                }
              }}
            />

            {empresaPickerOpen && activityCreatedId === null && (
              <div
                id="crm-empresa-resultados"
                role="listbox"
                className="absolute z-30 mt-1 max-h-64 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white p-1 shadow-lg"
              >
                {empresasLoading ? (
                  <p className="px-3 py-3 text-sm text-gray-500">
                    Buscando empresas…
                  </p>
                ) : empresas.length === 0 ? (
                  <p className="px-3 py-3 text-sm text-gray-500">
                    No hay empresas que coincidan con la búsqueda.
                  </p>
                ) : (
                  empresas.map((empresa) => (
                    <button
                      key={empresa.id}
                      type="button"
                      role="option"
                      aria-selected={empresaId === String(empresa.id)}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => selectEmpresa(empresa)}
                      className="block w-full rounded-md px-3 py-2 text-left hover:bg-blue-50 focus:bg-blue-50 focus:outline-none"
                    >
                      <span className="block text-sm font-medium text-gray-900">
                        {empresa.nombreComercial || empresa.razonSocial}
                      </span>
                      <span className="mt-0.5 block text-xs text-gray-500">
                        {[
                          empresa.idExterno,
                          empresa.cif,
                          empresa.emailContacto,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700">
              Tipo *
            </span>
            <select
              value={tipo}
              onChange={(event) => {
                const nextType = event.target.value;
                setTipo(nextType);
                if (createTask) {
                  setTaskForm((current) => ({
                    ...current,
                    tipo:
                      nextType === "llamada" ||
                      nextType === "reunion" ||
                      nextType === "email"
                        ? nextType
                        : "tarea",
                  }));
                }
              }}
              disabled={activityCreatedId !== null}
              className="min-h-11 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
            >
              <option value="llamada">Llamada</option>
              <option value="reunion">Reunión</option>
              <option value="email">Email</option>
              <option value="nota">Nota</option>
              <option value="otro">Otra</option>
            </select>
          </label>

          <Input
            label="Fecha y hora *"
            type="datetime-local"
            value={realizadaAt}
            onChange={(event) => setRealizadaAt(event.target.value)}
            disabled={activityCreatedId !== null}
          />

          <div className="md:col-span-2">
            <Input
              label="Resumen *"
              placeholder="Ej.: He hablado con Pedro sobre la propuesta de RC"
              value={asunto}
              onChange={(event) => setAsunto(event.target.value)}
              disabled={activityCreatedId !== null}
            />
          </div>

          <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700">
              Contacto
            </span>
            <select
              value={contactoId}
              onChange={(event) => setContactoId(event.target.value)}
              disabled={activityCreatedId !== null || !empresaId || loading}
              className="min-h-11 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm disabled:bg-gray-100"
            >
              <option value="">Sin contacto concreto</option>
              {contactos.map((contacto) => (
                <option key={contacto.id} value={contacto.id}>
                  {contacto.nombre}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700">
              Oportunidad
            </span>
            <select
              value={oportunidadId}
              onChange={(event) => setOportunidadId(event.target.value)}
              disabled={activityCreatedId !== null || !empresaId || loading}
              className="min-h-11 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm disabled:bg-gray-100"
            >
              <option value="">Sin oportunidad concreta</option>
              {oportunidades.map((oportunidad) => (
                <option key={oportunidad.id} value={oportunidad.id}>
                  {oportunidad.idExterno} · {oportunidad.titulo}
                </option>
              ))}
            </select>
          </label>

          <div className="md:col-span-2">
            <Input
              label="Detalle"
              value={detalle}
              onChange={(event) => setDetalle(event.target.value)}
              disabled={activityCreatedId !== null}
            />
          </div>

          <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4 md:col-span-2">
            <input
              type="checkbox"
              checked={createTask}
              disabled={activityCreatedId !== null}
              onChange={(event) => {
                const checked = event.target.checked;
                setCreateTask(checked);
                if (checked) {
                  setTaskForm((current) => ({
                    ...current,
                    tipo:
                      tipo === "llamada" ||
                      tipo === "reunion" ||
                      tipo === "email"
                        ? tipo
                        : "tarea",
                  }));
                } else {
                  setTaskForm((current) => ({
                    ...current,
                    syncCalendar: false,
                  }));
                }
              }}
              className="mt-1 h-4 w-4"
            />
            <span>
              <span className="block text-sm font-medium text-gray-900">
                Programar próxima acción
              </span>
              <span className="block text-xs text-gray-600">
                Crea una tarea vinculada a esta actividad para no perder el seguimiento.
              </span>
            </span>
          </label>

          {createTask && (
            <div className="space-y-4 rounded-lg border border-blue-200 bg-blue-50 p-4 md:col-span-2">
              <div>
                <h3 className="text-sm font-semibold text-blue-950">
                  Próxima acción
                </h3>
                <p className="mt-1 text-xs text-blue-800">
                  Quedará relacionada con la misma empresa, contacto y oportunidad.
                </p>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <label className="block md:col-span-2">
                  <span className="mb-1 block text-sm font-medium text-gray-700">
                    Tipo de próxima acción
                  </span>
                  <select
                    value={taskForm.tipo}
                    onChange={(event) =>
                      setTaskForm((current) => ({
                        ...current,
                        tipo: event.target.value as
                          | "tarea"
                          | "llamada"
                          | "reunion"
                          | "email",
                      }))
                    }
                    className="min-h-11 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                  >
                    <option value="tarea">Tarea general</option>
                    <option value="llamada">Llamada</option>
                    <option value="reunion">Reunión</option>
                    <option value="email">Email</option>
                  </select>
                </label>
                <div className="md:col-span-2">
                  <Input
                    label="Título de la tarea *"
                    placeholder="Ej.: Llamar para confirmar la propuesta"
                    value={taskForm.titulo}
                    onChange={(event) =>
                      setTaskForm((current) => ({
                        ...current,
                        titulo: event.target.value,
                      }))
                    }
                  />
                </div>

                <Input
                  label="Fecha y hora *"
                  type="datetime-local"
                  value={taskForm.venceAt}
                  onChange={(event) =>
                    setTaskForm((current) => ({
                      ...current,
                      venceAt: event.target.value,
                    }))
                  }
                />

                <label className="block">
                  <span className="mb-1 block text-sm font-medium text-gray-700">
                    Prioridad
                  </span>
                  <select
                    value={taskForm.prioridad}
                    onChange={(event) =>
                      setTaskForm((current) => ({
                        ...current,
                        prioridad: event.target.value as
                          | "baja"
                          | "normal"
                          | "alta",
                      }))
                    }
                    className="min-h-11 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                  >
                    <option value="baja">Baja</option>
                    <option value="normal">Normal</option>
                    <option value="alta">Alta</option>
                  </select>
                </label>

                <Input
                  label="Aviso previo (opcional)"
                  type="datetime-local"
                  value={taskForm.recordatorioAt}
                  onChange={(event) =>
                    setTaskForm((current) => ({
                      ...current,
                      recordatorioAt: event.target.value,
                    }))
                  }
                />

                <Input
                  label="Detalle de la tarea"
                  value={taskForm.detalle}
                  onChange={(event) =>
                    setTaskForm((current) => ({
                      ...current,
                      detalle: event.target.value,
                    }))
                  }
                />
              </div>

              {calendarConnected ? (
                <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-blue-300 bg-white p-3">
                  <input
                    type="checkbox"
                    checked={taskForm.syncCalendar}
                    onChange={(event) =>
                      setTaskForm((current) => ({
                        ...current,
                        syncCalendar: event.target.checked,
                      }))
                    }
                    className="mt-1 h-4 w-4"
                  />
                  <span>
                    <span className="block text-sm font-medium text-blue-950">
                      Añadir también a Google Calendar
                    </span>
                    <span className="block text-xs text-blue-700">
                      Si Google falla, la actividad y la tarea seguirán guardadas en el CRM.
                    </span>
                  </span>
                </label>
              ) : (
                <p className="text-xs text-blue-800">
                  Puedes conectar tu calendario personal desde Cartera CRM → Agenda.
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
